import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { Compass, KeyRound, ArrowRight, ShieldAlert } from "lucide-react";
import { ChapterBadge } from "@/components/PublicChrome";

/* -----------------------------------------------------------
   Jouer — Écran d'entrée du code d'accès 8 caractères
   Thème "Aventure & Temps" · mobile-first, gros boutons
   ----------------------------------------------------------- */

export default function Jouer() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    const c = code.toUpperCase();
    if (c.length !== 8) {
      setError("Le code d'accès fait exactement 8 caractères.");
      return;
    }
    setLoading(true);
    try {
      await gameApi.byCode(c);
      navigate(`/jouer/${c}`);
    } catch (err) {
      setError(err.response?.data?.detail || "Code invalide ou inconnu.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
      data-testid="jouer-entry"
    >
      {/* Header ultra-simple (pas de PublicHeader complet pour rester focus jeu) */}
      <header className="flex items-center justify-between px-5 h-14 border-b border-or/15 bg-noir-100/60">
        <Link to="/" className="flex items-center gap-2 group" data-testid="jouer-home">
          <Compass className="w-4 h-4 text-or group-hover:rotate-45 transition-transform duration-500" />
          <span className="font-display text-parchemin text-base tracking-wide">
            Picnic<span className="text-or">·</span>Game
          </span>
        </Link>
      </header>

      {/* Décor : boussole en filigrane */}
      <main className="flex-1 flex items-center justify-center px-5 py-10 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          aria-hidden="true"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 600'><g fill='none' stroke='%23C8922A' stroke-width='0.6'><circle cx='300' cy='300' r='240'/><circle cx='300' cy='300' r='180'/><circle cx='300' cy='300' r='120'/><path d='M300 60 L315 300 L300 540 L285 300 Z' fill='%23C8922A' fill-opacity='0.4'/></g></svg>\")",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            backgroundSize: "560px auto",
          }}
        />

        <div className="relative w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-5">
              <ChapterBadge testId="jouer-chapter">
                Le seuil · Entrée joueur
              </ChapterBadge>
            </div>
            <h1 className="font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight">
              Bienvenue,
              <br />
              <span className="italic text-or">aventurier.</span>
            </h1>
            <p className="mt-4 text-sm text-parchemin/60 max-w-xs mx-auto">
              Entrez votre code d&apos;accès reçu par email pour franchir le
              seuil et démarrer votre aventure.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-5 bg-noir-50/60 border border-or/25 rounded-sm p-6 md:p-8 backdrop-blur-sm shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]"
            data-testid="code-form"
          >
            <div className="flex flex-col gap-3">
              <label
                htmlFor="code-input"
                className="font-mono text-[10px] tracking-[0.25em] uppercase text-or/80 flex items-center gap-2"
              >
                <KeyRound className="w-3.5 h-3.5" />
                Code d&apos;accès · 8 caractères
              </label>
              <input
                id="code-input"
                data-testid="code-acces-input"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck="false"
                autoFocus
                value={code}
                onChange={(e) =>
                  setCode(
                    e.target.value
                      .toUpperCase()
                      .replace(/[^A-Z0-9]/g, "")
                      .slice(0, 8)
                  )
                }
                placeholder="XXXXXXXX"
                className="w-full px-4 py-5 bg-noir border-2 border-or/25 rounded-sm text-or font-mono font-bold text-3xl tracking-[0.35em] text-center outline-none focus:border-or transition placeholder:text-or/25"
              />
              <div className="flex items-center justify-center gap-1.5">
                {Array.from({ length: 8 }).map((_, i) => (
                  <span
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      i < code.length ? "bg-or" : "bg-or/20"
                    }`}
                  />
                ))}
              </div>
            </div>

            {error && (
              <div
                data-testid="error-msg"
                className="flex items-start gap-2 p-3 rounded-sm bg-oxyde/10 border border-oxyde/40 text-parchemin/90 text-sm"
              >
                <ShieldAlert className="w-4 h-4 text-oxyde mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || code.length !== 8}
              data-testid="entrer-partie-btn"
              className={`w-full inline-flex items-center justify-center gap-2 py-4 rounded-sm font-medium tracking-wide text-base transition-all ${
                code.length === 8 && !loading
                  ? "bg-or hover:bg-or-100 text-noir shadow-[0_14px_40px_-10px_rgba(200,146,42,0.6)]"
                  : "bg-or/25 text-noir/40 cursor-not-allowed"
              }`}
            >
              {loading ? (
                "Vérification…"
              ) : (
                <>
                  Franchir le seuil
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>

          <div className="mt-6 p-4 border border-or/15 rounded-sm bg-noir-50/40 text-sm text-parchemin/70">
            <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-or/70 mb-2">
              Pas encore de code ?
            </div>
            <p className="leading-relaxed">
              Réservez votre aventure sur{" "}
              <Link
                to="/reserver"
                data-testid="jouer-cta-reserver"
                className="text-or underline decoration-or/40 hover:decoration-or transition"
              >
                la page de réservation
              </Link>{" "}
              — vous recevrez votre code par email 24h avant le départ.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
